import { PREV_BRAND_OTHERS } from '@/lib/otherBrandsStorage';

/** localStorage for /7_2 — LBS: usual smoke brand; Dice: like-most single choice. */
export const Q7_2_STORAGE_KEY = 'npdsurvey:q7_2';

const VALUE_SEP = '|||';

/** Dice /7_2 option labels (canonical stored values). */
export const Q7_2_OPTIONS_DICE = [
  'Taste/flavour',
  'Overall experience',
  'Appearance/pack',
  'Something different from my usual choice',
  'Nothing in particular',
] as const;

export type Q7_2OptionDice = (typeof Q7_2_OPTIONS_DICE)[number];

export type Q7_2Answers = {
  brand: string;
  variant: string;
  /**
   * API shape (`selected: string[]` length 1).
   * LBS: `"Brand - Variant"` (or Others free-text).
   * Dice: one of `Q7_2_OPTIONS_DICE`.
   */
  selected: string[];
};

export function isQ7_2DiceOption(value: string): value is Q7_2OptionDice {
  return (Q7_2_OPTIONS_DICE as readonly string[]).includes(value);
}

export function encodeQ7_2SelectValue(brand: string, variant: string): string {
  return `${brand}${VALUE_SEP}${variant}`;
}

export function decodeQ7_2SelectValue(value: string): { brand: string; variant: string } | null {
  const idx = value.indexOf(VALUE_SEP);
  if (idx <= 0) return null;
  const brand = value.slice(0, idx).trim();
  const variant = value.slice(idx + VALUE_SEP.length).trim();
  if (!brand || !variant) return null;
  return { brand, variant };
}

export function formatQ7_2Selection(brand: string, variant: string): string {
  if (brand === PREV_BRAND_OTHERS) {
    return variant.trim();
  }
  return `${brand.trim()} - ${variant.trim()}`;
}

export function buildQ7_2Answers(brand: string, variant: string): Q7_2Answers {
  const b = brand.trim();
  const v = variant.trim();
  const normalizedBrand = b.toLowerCase() === PREV_BRAND_OTHERS.toLowerCase() ? PREV_BRAND_OTHERS : b;
  const label = formatQ7_2Selection(normalizedBrand, v);
  return {
    brand: normalizedBrand,
    variant: v,
    selected: label ? [label] : [],
  };
}

export function buildQ7_2DiceAnswers(option: Q7_2OptionDice): Q7_2Answers {
  return {
    brand: '',
    variant: '',
    selected: [option],
  };
}

export function readQ7_2Answers(): Q7_2Answers | null {
  try {
    const raw = localStorage.getItem(Q7_2_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as unknown;
    if (typeof o !== 'object' || o === null) return null;
    const p = o as { brand?: unknown; variant?: unknown; selected?: unknown };

    if (Array.isArray(p.selected) && p.selected.length > 0 && typeof p.selected[0] === 'string') {
      const label = p.selected[0].trim();
      if (isQ7_2DiceOption(label)) {
        return buildQ7_2DiceAnswers(label);
      }
    }

    if (typeof p.brand === 'string' && typeof p.variant === 'string' && p.brand.trim() && p.variant.trim()) {
      return buildQ7_2Answers(p.brand, p.variant);
    }

    /** Migrate prior brand-variant labels only (`Brand - Variant`). */
    if (Array.isArray(p.selected) && p.selected.length > 0 && typeof p.selected[0] === 'string') {
      const label = p.selected[0].trim();
      const dash = label.indexOf(' - ');
      if (dash > 0) {
        return buildQ7_2Answers(label.slice(0, dash), label.slice(dash + 3));
      }
    }

    return null;
  } catch {
    return null;
  }
}

export function writeQ7_2Answers(data: Q7_2Answers): void {
  try {
    if (data.selected.length === 1 && isQ7_2DiceOption(data.selected[0])) {
      localStorage.setItem(Q7_2_STORAGE_KEY, JSON.stringify(buildQ7_2DiceAnswers(data.selected[0])));
      return;
    }
    localStorage.setItem(Q7_2_STORAGE_KEY, JSON.stringify(buildQ7_2Answers(data.brand, data.variant)));
  } catch {
    /* ignore */
  }
}

export function clearQ7_2Answers(): void {
  try {
    localStorage.removeItem(Q7_2_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function isQ7_2Complete(): boolean {
  const a = readQ7_2Answers();
  if (a == null || a.selected.length !== 1) return false;
  if (isQ7_2DiceOption(a.selected[0])) return true;
  return a.brand.trim().length > 0 && a.variant.trim().length > 0;
}
